import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import AppShell from "@/components/AppShell";
import api, { getToken } from "@/lib/api";
import { useLang } from "@/i18n";
import { Download, FileAudio, FileVideo, ImageIcon, FileText, FileType, Trash2 } from "lucide-react";

const iconFor = (cat) => ({ audio: FileAudio, video: FileVideo, image: ImageIcon, pdf: FileType, text: FileText }[cat] || FileText);
const fmtBytes = (b) => { const u=["B","KB","MB","GB","TB"]; let i=0,n=b||0; while(n>=1024&&i<u.length-1){n/=1024;i++} return `${n.toFixed(n>=100?0:1)} ${u[i]}`;};

export default function SpaceVault() {
  const { t } = useLang();
  const nav = useNavigate();
  const [tab, setTab] = useState("files");
  const [files, setFiles] = useState([]);
  const [caps, setCaps] = useState([]);
  const [txs, setTxs] = useState([]);

  const load = async () => {
    const [f, c, x] = await Promise.all([
      api.get("/files"), api.get("/capsules"), api.get("/transmissions"),
    ]);
    setFiles(f.data); setCaps(c.data); setTxs(x.data);
  };
  useEffect(() => { load(); }, []);

  const del = async (id) => {
    await api.delete(`/files/${id}`);
    load();
  };

  const tabs = [
    { key: "files", label: t("vault.tab_files"), count: files.length },
    { key: "capsules", label: t("vault.tab_capsules"), count: caps.length },
    { key: "tx", label: t("vault.tab_tx"), count: txs.length },
  ];

  return (
    <AppShell>
      <div className="mb-8">
        <div className="label-overline">Encrypted Archive</div>
        <h1 className="font-display font-black text-4xl tracking-tighter mt-2" data-testid="vault-title">{t("vault.title")}</h1>
        <p className="text-white/55 mt-2">{t("vault.subtitle")}</p>
      </div>

      <div className="flex gap-2 mb-6 border-b border-white/5">
        {tabs.map((tb) => (
          <button
            key={tb.key}
            onClick={() => setTab(tb.key)}
            data-testid={`vault-tab-${tb.key}`}
            className={`px-4 py-3 font-mono-data text-[11px] tracking-[0.2em] uppercase ${
              tab === tb.key ? "text-[#00D4FF] border-b-2 border-[#00D4FF]" : "text-white/50 hover:text-white"
            }`}
          >
            {tb.label} <span className="opacity-60">({tb.count})</span>
          </button>
        ))}
      </div>

      {tab === "files" && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="vault-files">
          {files.length === 0 && <div className="text-white/40 col-span-full">{t("vault.empty")}</div>}
          {files.map((f) => {
            const Icon = iconFor(f.category);
            return (
              <div key={f.id} className="holo-panel p-4 flex items-center gap-3">
                <div className="w-10 h-10 grid place-items-center border border-[#00D4FF]/40">
                  <Icon size={16} className="text-[#00D4FF]" />
                </div>
                <div className="min-w-0 flex-1">
                  <div className="text-sm truncate">{f.original_filename}</div>
                  <div className="font-mono-data text-[10px] tracking-[0.16em] uppercase text-white/50 mt-0.5">{f.category} · {fmtBytes(f.size)}</div>
                </div>
                <a
                  href={`${process.env.REACT_APP_BACKEND_URL}/api/files/${f.id}/download?auth=${encodeURIComponent(getToken() || "")}`}
                  target="_blank" rel="noreferrer"
                  className="text-white/55 hover:text-[#00D4FF]"
                  data-testid={`vault-download-${f.id}`}
                ><Download size={14} /></a>
                <button onClick={() => del(f.id)} className="text-white/40 hover:text-[#FF3366]" data-testid={`vault-delete-${f.id}`}>
                  <Trash2 size={14} />
                </button>
              </div>
            );
          })}
        </div>
      )}

      {tab === "capsules" && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="vault-capsules">
          {caps.length === 0 && <div className="text-white/40 col-span-full">{t("vault.empty")}</div>}
          {caps.map((c) => (
            <div key={c.id} className="holo-panel p-5">
              <div className="label-overline">{c.status}</div>
              <div className="font-display font-bold text-lg mt-2 tracking-tight">{c.capsule_name}</div>
              <div className="text-white/55 text-sm mt-2 line-clamp-3">{c.summary}</div>
              <div className="mt-4 font-mono-data text-[11px] tracking-[0.18em] uppercase text-white/55">{c.file_count} files · {fmtBytes(c.total_size)}</div>
            </div>
          ))}
        </div>
      )}

      {tab === "tx" && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="vault-tx">
          {txs.length === 0 && <div className="text-white/40 col-span-full">{t("vault.empty")}</div>}
          {txs.map((x) => (
            <button
              key={x.transmission_id}
              onClick={() => nav(`/transmission/${x.transmission_id}`)}
              className="holo-panel p-5 text-left hover:border-[#00D4FF]/40"
              data-testid={`vault-tx-${x.transmission_id}`}
            >
              <div className="font-mono-data text-[11px] text-[#00D4FF] tracking-[0.18em]">{x.transmission_id}</div>
              <div className="font-display font-bold text-lg mt-2 tracking-tight">{x.capsule_name}</div>
              <div className="font-mono-data text-[10px] tracking-[0.18em] uppercase text-white/55 mt-2">{x.status.replace(/_/g, " ")}</div>
            </button>
          ))}
        </div>
      )}
    </AppShell>
  );
}
